/**
 * TEMPL — custom frequency dropdown (Cadence-style)
 * =================================================
 * Aug 27 2026. Replaces the LOOK of Loop's native <select> without taking over
 * what it does.
 *
 * WHY NOT JUST STYLE THE SELECT
 * -----------------------------
 * A native <select> opens the operating system's menu. Its rows cannot be
 * styled, spaced, animated or given a second column. Cadence's dropdown feels
 * smoother because it is not a select at all — it is a button plus a list of
 * buttons, with a hidden native select kept purely to carry the value.
 *
 * HOW THIS STAYS SAFE
 * -------------------
 * We do NOT post the selling plan ourselves. Loop keeps a hidden
 * <input name="selling_plan"> inside the product form and updates it from its
 * own listener on its <select>. So this file only ever does:
 *
 *     select.value = chosen;
 *     select.dispatchEvent(new Event('change', { bubbles: true }));
 *
 * Verified on the preview theme Aug 27: driving the select this way flipped the
 * product form's hidden selling_plan field to the matching plan ID. Loop stays
 * in charge of the purchase; we only change what the shopper sees.
 *
 * If this file fails or is removed, .templ-has-freq is never added, the CSS
 * stops hiding Loop's own row, and the native select comes straight back.
 */

const SUB_OPTION = '.loop-widget-purchase-option[data-loop-widget-selling-plan-group]';
const BOX_CLASS = 'templ-freq';
const LABEL_PREFIX = 'Deliver Every:';

/**
 * The list renders in the browser's top layer, via the popover API, because
 * nothing else could win the paint-order race against the ADD TO CART button.
 * See THE STACKING PROBLEM in templ-widget-frequency.css for the three CSS fixes
 * that were tried first and all failed.
 *
 * Feature-detected rather than assumed: without it we fall back to the old
 * in-flow list, which is imperfect but usable.
 */
const SUPPORTS_POPOVER = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

const CARET =
  '<svg class="templ-freq-caret" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * Loop's details container carries overflow:hidden so it can collapse cleanly.
 * That clips our absolutely positioned list. We lift the clip only while the
 * list is actually open, so Loop's collapse behaviour is untouched the rest of
 * the time. See the long comment in templ-widget-frequency.css.
 */
function clipHost(box) {
  return box.closest('.loop-widget-spg-container');
}

/**
 * Mirrors "a frequency menu is open" onto <body>, so CSS elsewhere on the page
 * can react to it. Currently one consumer: the sticky add-to-cart bar drops a
 * layer while the menu is open, because the menu is trapped inside
 * .product-details' stacking context and cannot climb above the bar on its own.
 * See THE STACKING PROBLEM in templ-widget-frequency.css.
 *
 * This exists as a class rather than a :has() selector for a concrete reason:
 * the :has() version matched but did not repaint, so the bar only dropped once
 * some other style recalculation happened — in practice, when the cursor left
 * the control. A class toggle invalidates immediately.
 *
 * Reads the DOM rather than tracking state, so it stays correct no matter which
 * path opened or closed the box (button, option click, outside click, Escape).
 */
function syncBodyState() {
  const anyOpen = !!document.querySelector('.' + BOX_CLASS + '.is-open');
  document.body.classList.toggle('templ-freq-menu-open', anyOpen);
}

/**
 * The top layer is positioned against the viewport, not against the button, so
 * the anchor position has to be written in by hand. Kept in sync while open by
 * the listeners in trackAnchor().
 */
function positionList(box) {
  const btn = box.querySelector('.templ-freq-btn');
  const list = box.querySelector('.templ-freq-list');
  const rect = btn.getBoundingClientRect();
  list.style.left = rect.left + 'px';
  list.style.top = rect.bottom + 4 + 'px';
  list.style.width = rect.width + 'px';
}

/** The box whose list is currently open in the top layer, if any. */
let anchoredBox = null;

function repositionAnchored() {
  if (anchoredBox) positionList(anchoredBox);
}

function trackAnchor(box) {
  anchoredBox = box;
  // Capture phase so scrolling of any ancestor container is caught, not just the
  // window. Passive: this only reads layout and writes inline styles.
  window.addEventListener('scroll', repositionAnchored, { capture: true, passive: true });
  window.addEventListener('resize', repositionAnchored, { passive: true });
}

function untrackAnchor() {
  anchoredBox = null;
  window.removeEventListener('scroll', repositionAnchored, { capture: true });
  window.removeEventListener('resize', repositionAnchored);
}

function close(box) {
  const list = box.querySelector('.templ-freq-list');
  box.classList.remove('is-open');
  box.querySelector('.templ-freq-btn').setAttribute('aria-expanded', 'false');
  clipHost(box)?.classList.remove('templ-freq-open');

  if (SUPPORTS_POPOVER) {
    untrackAnchor();
    // Guard: hidePopover() on an already-hidden popover throws.
    if (list.matches(':popover-open')) list.hidePopover();
  } else {
    list.hidden = true;
  }

  syncBodyState();
}

function open(box) {
  const list = box.querySelector('.templ-freq-list');
  box.classList.add('is-open');
  box.querySelector('.templ-freq-btn').setAttribute('aria-expanded', 'true');
  clipHost(box)?.classList.add('templ-freq-open');

  if (SUPPORTS_POPOVER) {
    positionList(box);   // before showing, so it never paints in the wrong place
    list.showPopover();
    trackAnchor(box);
  } else {
    list.hidden = false;
  }

  syncBodyState();
}

/**
 * Builds the box for one subscription option, or leaves it alone if it is
 * already correct. Idempotent via a signature of the select's options and
 * current value — this is what stops the MutationObserver below from looping,
 * and what makes extra passes free.
 */
function build(option) {
  const select = option.querySelector('select.loop-widget-sp-selector');
  const container = option.querySelector('.loop-widget-spg-container');
  if (!select || !container) return;

  const options = [...select.options];
  if (!options.length) return;

  const sig = options.map((o) => o.value + ':' + o.textContent.trim()).join('|') + '#' + select.value;
  const existing = container.querySelector(':scope > .' + BOX_CLASS);
  if (existing && existing.dataset.sig === sig) return; // already correct
  // Never tear down a box the shopper currently has open.
  if (existing && existing.classList.contains('is-open')) return;
  if (existing) existing.remove();

  const box = el('div', BOX_CLASS);
  box.dataset.sig = sig;

  // ---- button -----------------------------------------------------------
  const btn = el('button', 'templ-freq-btn');
  btn.type = 'button';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');

  const label = el('span', 'templ-freq-label');
  const value = el('strong', null, options.find((o) => o.value === select.value)?.textContent.trim() || '');
  label.append(document.createTextNode(LABEL_PREFIX + ' '), value);
  btn.append(label);
  btn.insertAdjacentHTML('beforeend', CARET);

  // ---- list -------------------------------------------------------------
  const list = el('ul', 'templ-freq-list');
  list.setAttribute('role', 'listbox');
  if (SUPPORTS_POPOVER) {
    // "manual", not "auto": auto light-dismisses on any outside click, which
    // would race the outside-click handler below and swallow option clicks.
    // A popover element is display:none until showPopover(), so no `hidden`.
    list.setAttribute('popover', 'manual');
  } else {
    list.hidden = true;
  }

  options.forEach((opt) => {
    const li = document.createElement('li');
    const b = el('button', 'templ-freq-opt');
    b.type = 'button';
    b.setAttribute('role', 'option');
    b.dataset.value = opt.value;
    const selected = opt.value === select.value;
    b.setAttribute('aria-selected', String(selected));
    if (selected) b.classList.add('is-selected');

    b.append(el('span', 'templ-freq-opt-name', opt.textContent.trim()));

    b.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      // Drive Loop's own control. Loop's listener updates the hidden
      // selling_plan field in the product form; we never write it ourselves.
      select.value = opt.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      value.textContent = opt.textContent.trim();
      list.querySelectorAll('.templ-freq-opt').forEach((n) => {
        const on = n.dataset.value === opt.value;
        n.classList.toggle('is-selected', on);
        n.setAttribute('aria-selected', String(on));
      });
      box.dataset.sig = options.map((o) => o.value + ':' + o.textContent.trim()).join('|') + '#' + opt.value;
      close(box);
      btn.focus();
    });

    li.append(b);
    list.append(li);
  });

  btn.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (box.classList.contains('is-open')) close(box);
    else open(box);
  });

  box.append(btn, list);
  container.append(box);
  option.classList.add('templ-has-freq'); // only now does CSS hide Loop's row
}

function runSync() {
  try {
    document.querySelectorAll(SUB_OPTION).forEach(build);
    // Self-heal: if Loop rebuilt the widget out from under an open box, the
    // body class would otherwise be left stranded on.
    syncBodyState();
  } catch (error) {
    console.warn('[templ-freq]', error);
  }
}

// Close on outside click and on Escape.
document.addEventListener('click', (event) => {
  document.querySelectorAll('.' + BOX_CLASS + '.is-open').forEach((box) => {
    if (!box.contains(event.target)) close(box);
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.' + BOX_CLASS + '.is-open').forEach(close);
});

/**
 * Same resilience pattern as the main widget file, and for the same reason:
 * Loop tears the widget down and rebuilds it over several frames when the
 * shopper changes flavour, so a single pass runs against DOM that Loop then
 * throws away. Immediate pass, trailing passes, plus a slow safety interval.
 * build() early-returns when the box is already correct, so extra passes are
 * free and the observer cannot loop.
 *
 * The observer watches childList/subtree only. build() adds a CLASS, and class
 * changes are attribute mutations — unobserved here on purpose. Observing
 * attributes and writing one from the callback is what turned the old
 * theme.liquid script into a permanent ~10/sec loop.
 */
const TRAILING_PASSES_MS = [300, 900, 1800, 3000];
let rafQueued = false;
let trailingTimers = [];

function scheduleSync() {
  if (!rafQueued) {
    rafQueued = true;
    requestAnimationFrame(() => {
      rafQueued = false;
      runSync();
    });
  }
  trailingTimers.forEach(clearTimeout);
  trailingTimers = TRAILING_PASSES_MS.map((ms) => setTimeout(runSync, ms));
}

setInterval(runSync, 2000);
new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });
document.addEventListener('change', scheduleSync, true);
document.addEventListener('DOMContentLoaded', scheduleSync);
scheduleSync();
